// 報價單管理服務
import { db } from '../cloudsql';

export interface Quote {
  id: string;
  quote_number: string;
  quote_name: string;
  customer_id: string;
  customer_name: string;
  customer_tax_id?: string;
  issue_date: string;
  valid_until: string;
  terms?: string;
  items?: unknown[];
  subtotal?: number;
  tax_rate?: number;
  tax_amount?: number;
  discount_rate?: number;
  discount_amount?: number;
  total_discount?: number;
  total_amount?: number;
  discounted_total?: number;
  // 新增的自動計算欄位
  subtotal_amount?: number; // 小計金額（sum of quote_items.subtotal）
  subtotal_discount?: number; // 折扣金額（sum of quote_items.subtotal_discount）
  subtotal_afterdiscount?: number; // 折扣後小計金額（sum of quote_items.subtotal_afterdiscount）
  status?: 'draft' | 'sent' | 'signed_back' | 'failed' | 'expired';
  contact_email?: string;
  contact_name?: string;
  contact_id?: string;
  contacts?: unknown[];
  contact_ids?: string[];
  primary_contact_id?: string;
  project_id?: string;
  contract_id?: string;
  signed_back_file_url?: string;
  signed_back_date?: string;
  creator_name?: string;
  creator_email?: string;
  created_at: string;
  updated_at: string;
}

export interface QuoteItem {
  id: string;
  quote_id: string;
  service_type: string;
  item_name: string;
  list_price: number;
  unit_price?: number; // 牌價（從 service_rate 取得，當年度）
  contract_price: number;
  quantity: number;
  unit?: string;
  is_deduction?: boolean;
  // 新增的自動計算欄位
  subtotal?: number; // 小計金額（自動計算：unit_price * quantity）
  subtotal_discount?: number; // 折扣金額（自動計算：(unit_price - contract_price) * quantity）
  subtotal_afterdiscount?: number; // 折扣後小計金額（自動計算：contract_price * quantity）
  created_at: string;
  updated_at: string;
}

export class QuotesService {
  // 取得所有報價單
  async getAll(orderBy?: { field: string; direction: 'asc' | 'desc' }[]): Promise<Quote[]> {
    return db.query<Quote>('quotes', {
      orderBy: orderBy || [{ field: 'created_at', direction: 'desc' }]
    });
  }

  // 依客戶 ID 取得報價單
  async getByCustomerId(customerId: string): Promise<Quote[]> {
    return db.query<Quote>('quotes', {
      where: { customer_id: customerId },
      orderBy: [{ field: 'created_at', direction: 'desc' }]
    });
  }

  // 依狀態取得報價單
  async getByStatus(status: string): Promise<Quote[]> {
    return db.query<Quote>('quotes', {
      where: { status },
      orderBy: [{ field: 'created_at', direction: 'desc' }]
    });
  }

  // 依 ID 取得報價單
  async getById(id: string): Promise<Quote | null> {
    return db.getById<Quote>('quotes', id);
  }

  // 依報價單號取得報價單
  async getByQuoteNumber(quoteNumber: string): Promise<Quote | null> {
    const results = await db.query<Quote>('quotes', {
      where: { quote_number: quoteNumber },
      limit: 1
    });
    return results.length > 0 ? results[0] : null;
  }

  // 建立報價單
  async create(data: Omit<Quote, 'id' | 'created_at' | 'updated_at'>): Promise<Quote> {
    return db.create<Quote>('quotes', data);
  }

  // 更新報價單
  async update(id: string, data: Partial<Quote>): Promise<Quote> {
    return db.update<Quote>('quotes', id, data);
  }

  // 刪除報價單
  async delete(id: string): Promise<void> {
    return db.delete('quotes', id);
  }

  // 報價項目相關方法
  async getQuoteItems(quoteId: string): Promise<QuoteItem[]> {
    return db.query<QuoteItem>('quote_items', {
      where: { quote_id: quoteId },
      orderBy: [{ field: 'created_at', direction: 'asc' }]
    });
  }

  async createQuoteItem(data: Omit<QuoteItem, 'id' | 'created_at' | 'updated_at'>): Promise<QuoteItem> {
    return db.create<QuoteItem>('quote_items', data);
  }

  async updateQuoteItem(id: string, data: Partial<QuoteItem>): Promise<QuoteItem> {
    return db.update<QuoteItem>('quote_items', id, data);
  }

  async deleteQuoteItem(id: string): Promise<void> {
    return db.delete('quote_items', id);
  }

  // 批次操作報價項目
  async batchQuoteItems(operations: Array<{
    type: 'create' | 'update' | 'delete';
    id?: string;
    data?: unknown;
  }>): Promise<unknown[]> {
    return db.batch(
      operations.map(op => ({
        type: op.type,
        table: 'quote_items',
        id: op.id,
        data: op.data
      }))
    );
  }
}

// 匯出單例
export const quotesService = new QuotesService();


